/// <reference types="office-js" />
/**
 * Theme bootstrapping. Runs at module scope from main.tsx — before React's
 * first paint and independent of OfficeProvider, because the recorder and
 * pdf-viewer roots skip that provider entirely.
 *
 * Resolution order:
 * 1. OS preference immediately (also covers plain-browser dev without Office).
 * 2. Once Office is ready, the Outlook host theme takes over permanently when
 *    it is available (`officeTheme` needs a host that exposes it; the
 *    OfficeThemeChanged event needs Mailbox 1.14 and a task pane).
 */
import { useThemeStore } from '@/utils/stores/useThemeStore';

import { getSystemTheme, resolveThemeFromOfficeTheme } from './theme';

let initialized = false;

function setThemeFromOffice(): boolean {
  const resolved = resolveThemeFromOfficeTheme(Office.context?.officeTheme);
  if (resolved === null) {
    return false;
  }
  useThemeStore.getState().setTheme(resolved, 'office');
  return true;
}

function watchSystemTheme(): void {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return;
  }
  const query = window.matchMedia('(prefers-color-scheme: dark)');
  if (typeof query.addEventListener !== 'function') {
    return;
  }
  query.addEventListener('change', (event) => {
    const { setTheme, source } = useThemeStore.getState();
    // The Outlook host theme, once known, outranks OS preference changes.
    if (source === 'system') {
      setTheme(event.matches ? 'dark' : 'light', 'system');
    }
  });
}

function watchOfficeTheme(): void {
  // OfficeThemeChanged requires Mailbox 1.14 and only fires in task panes.
  // The manifest's MinVersion is far lower, so feature-detect at runtime —
  // same pattern as addItemChangedHandler's Mailbox 1.5 guard.
  if (!Office.context?.mailbox || !Office.context.requirements?.isSetSupported('Mailbox', '1.14')) {
    return;
  }

  try {
    Office.context.mailbox.addHandlerAsync(
      Office.EventType.OfficeThemeChanged,
      (args: { officeTheme?: Office.OfficeTheme; type: string }) => {
        const resolved = resolveThemeFromOfficeTheme(args.officeTheme) ?? getSystemTheme();
        useThemeStore.getState().setTheme(resolved, 'office');
      },
      (asyncResult) => {
        if (asyncResult.status === Office.AsyncResultStatus.Failed) {
          console.error('Failed to add OfficeThemeChanged handler:', asyncResult.error);
        }
      },
    );
  } catch (error) {
    console.error('Error registering OfficeThemeChanged handler:', error);
  }
}

export function initTheme(): void {
  if (initialized) {
    return;
  }
  initialized = true;

  const store = useThemeStore.getState();
  const systemTheme = getSystemTheme();
  if (systemTheme !== store.theme) {
    store.setTheme(systemTheme, 'system');
  }
  watchSystemTheme();

  if (typeof Office === 'undefined' || typeof Office.onReady !== 'function') {
    return;
  }

  void Office.onReady()
    .then(() => {
      setThemeFromOffice();
      watchOfficeTheme();
    })
    .catch((error: unknown) => {
      console.error('Theme init: Office.onReady failed:', error);
    });
}

/** Test-only: allow initTheme to run again with fresh stubs. */
export function resetThemeInitForTests(): void {
  initialized = false;
}
